import Input from 'MediaManager/components/Form/Input'
import ScreenLayout from 'MediaManager/layouts/ScreenLayout'
import React, { useEffect, useMemo, useState } from 'react'
import Checkbox from 'shared/components/Checkbox'
import LevelsEditor from './components/Levels'
import Button from 'shared/components/Button'
import FileUpload from 'MediaManager/components/FileUpload'
import Toggle from 'shared/components/Toggle'
import { getPropertyById, getUnitType } from 'MediaManager/services/property'
import { Link, useSearch } from '@tanstack/react-location'
import { useFormik } from 'formik'
import Loader from 'shared/components/Loader'
import {
  deleteUnitType,
  deleteUnitTypesConfiguration,
  getUnitTypesConfigurationOptionsFields,
  upsertUnitType,
  upsertUnitTypesConfiguration,
} from 'MediaManager/services/unitType'
import ActionSuccessModal from 'MediaManager/components/ActionSuccessModal'
import { convertStringNumbersToNumbers, snakeCaseToTitleCase } from 'shared/helpers/general'
import * as Yup from 'yup'
import { errorMessages } from 'shared/settings'
import { toast } from 'react-toastify'

const unitTypeMainFields = [
  'name',
  'nick_name',
  'description',
  'number_of_bathrooms',
  'number_of_bedrooms',
  'details_json',
  'size',
  'size_to_display',
]

function LineEditScreen() {
  const [line, setLine] = useState()
  const { id, property_id } = useSearch()
  const [property, setProperty] = useState()
  const [resultValue, setResultValue] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [unitTypesConfigurationOptionsFields, setUnitTypesConfigurationOptionsFields] = useState([])

  useEffect(() => {
    ;(async () => {
      const data = await getUnitTypesConfigurationOptionsFields()
      setUnitTypesConfigurationOptionsFields(data.filter((data) => !['unit_type'].includes(data.name)))
    })()
  }, [])

  useEffect(() => {
    if (unitTypesConfigurationOptionsFields?.length > 0) {
      if (property_id) {
        ;(async () => {
          const { data } = await getPropertyById(property_id)
          setProperty(data.properties_by_id)
          setIsLoading(false)
        })()
      }
      if (id) {
        ;(async () => {
          const { data } = await getUnitType(
            id,
            unitTypesConfigurationOptionsFields.map((f) => f.name),
          )
          setProperty(data.unit_types[0].property)
          setLine(data.unit_types[0])
          setIsLoading(false)
          formik.setValues({
            ...unitTypeMainFields
              .filter((f) => !['details_json'].includes(f))
              .reduce((acc, field) => {
                acc[field] = data.unit_types[0][field]
                return acc
              }, {}),
            ...unitTypesConfigurationOptionsFields.reduce((acc, field) => {
              acc[field.name] = data.unit_types[0].unit_types_configuration_options[field.name]
              return acc
            }, {}),
          })
        })()
      }
    }
  }, [property_id, id, unitTypesConfigurationOptionsFields])

  const validationSchema = Yup.object().shape({
    description: Yup.string().required(errorMessages.required),
    name: Yup.string().required(errorMessages.required),
  })

  const formik = useFormik({
    initialValues: {},
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema,
    initialTouched: false,
    validateOnMount: true,
    onSubmit: async () => {
      try {
        // upsert unit type
        const { data } = await upsertUnitType({
          ...(id ? { id: line?.id } : {}),
          property_id: property_id,
          unit_type: formik.values.description,

          ...convertStringNumbersToNumbers(
            unitTypeMainFields
              .reduce((acc, field) => {
                acc[field] = formik.values[field]
                return acc
              }, {}),
          ),
          size_to_display: formik.values.size_to_display ? String(formik.values.size_to_display) : undefined,
          nick_name: formik.values.nick_name ? String(formik.values.nick_name) : undefined,
        })

        const upsertedUnitTypeId = data.upsert_unit_types[0].id
        // upsert unit type configuration options
        await upsertUnitTypesConfiguration({
          ...unitTypesConfigurationOptionsFields.reduce((acc, field) => {
            acc[field.name] = false
            return acc
          }, {}),
          id: line?.unit_types_configuration_options?.id,
          unit_type_id: upsertedUnitTypeId,
          ...Object.keys(formik.values).reduce((acc, field) => {
            if (!unitTypeMainFields.includes(field)) {
              acc[field] = formik.values[field] ?? false
              if (acc[field] === undefined) acc[field] = false
            }
            return acc
          }, {}),
        })
        setResultValue('success')
      } catch (error) {
        console.error(error)
        setResultValue('error')
      }
    },
  })

  const settings = useMemo(() => {
    return unitTypesConfigurationOptionsFields
      .filter((f) => !['id', 'unit_type'].includes(f.name))
      .map((field) => {
        return {
          label: snakeCaseToTitleCase(field.name),
          name: field.name,
        }
      })
  }, [unitTypesConfigurationOptionsFields])

  const isValid = useMemo(() => {
    return Object.values(formik.errors).every((v) => !v)
  }, [formik])

  function getFieldError(field) {
    return formik.errors[field] && formik.touched[field] ? formik.errors[field] : null
  }

  async function deleteLine() {
    if (window.confirm('Are you sure you want to delete this line?')) {
      try {
        await deleteUnitType(line.id)
        setResultValue('success')
      } catch (error) {
        setResultValue('error')
      }
    }
  }

  async function updateConfigurationOptions(key, value) {
    if (!id) {
      return
    }
    await upsertUnitTypesConfiguration({
      id: line?.unit_types_configuration_options.id,
      unit_type_id: id,
      [key]: value,
    })
    toast.success(`Configuration flags updated`, { theme: 'colored' })
  }

  return (
    <ScreenLayout title={id ? 'Edit line' : 'Add line'}>
      {resultValue && (
        <ActionSuccessModal
          action={`Line`}
          successText="has been successfully upserted."
          errorText="upserting failed. Please try again."
          state={resultValue}
          error={'Please try again later.'}
          skipConfirm
          skipBack
          onClose={() => {
            if (resultValue === 'success') {
              window.history.back()
            } else {
              setResultValue(null)
            }
          }}
        />
      )}
      {isLoading && (
        <div>
          <Loader />
        </div>
      )}
      {!isLoading && (
        <>
          <form onSubmit={formik.handleSubmit}>
            <div className="grid grid-cols-4 gap-[16px]">
              {property && (
                <Input label="Property" wrapClassName="col-span-4" disabled value={property.property_name} />
              )}
              {id && (
                <Input
                  label="ID"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={line?.id}
                  disabled
                  wrapClassName="col-span-1"
                />
              )}
              <Input
                label="Name"
                placeholder="Enter line’s descriptive name (letter, floor range etc.)"
                required
                name="name"
                error={getFieldError('name')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                wrapClassName={id ? 'col-span-1' : 'col-span-2'}
              />
              <Input
                label="Nick name"
                name="nick_name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.nick_name}
                placeholder="Enter line’s marketing name (if exists)"
                wrapClassName="col-span-2"
              />
              <Input
                label="Size"
                name="size"
                number
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.size || ''}
                placeholder="Enter line’s size"
                wrapClassName="col-span-2"
              />
              <Input
                label="Size to display"
                name="size_to_display"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.size_to_display || ''}
                placeholder="Enter line’s size to display"
                wrapClassName="col-span-2"
              />
              <Input
                label="Description"
                name="description"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
                placeholder="Enter description"
                wrapClassName="col-span-2"
                error={getFieldError('description')}
                required
              />
              <Input
                label="Number of bathrooms"
                name="number_of_bathrooms"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.number_of_bathrooms}
                placeholder="Enter Number of bathrooms"
                wrapClassName="col-span-1"
              />
              <Input
                label="Number of bedrooms"
                name="number_of_bedrooms"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.number_of_bedrooms}
                placeholder="Enter Number of bedrooms"
                wrapClassName="col-span-1"
              />
            </div>
            {id && (
              <div>
                <div className="flex items-center justify-between">
                  <div className="text-sm my-[16px] opacity-50">Levels</div>
                  <div className="text-sm my-[16px]">
                    <Link to={`/level/add?unit_type_id=${id}`}>
                      <Button rounded size="small" variant="secondary">
                        Add level
                      </Button>
                    </Link>
                  </div>
                </div>
                <LevelsEditor line={line} />
              </div>
            )}
            <div className="my-[16px] p-[16px] border-[1px] border-[#e0e0e0] rounded-[10px] grid grid-cols-3 gap-[16px]">
              {settings.map((o) => (
                <div className="flex items-center">
                  <Checkbox
                    name={o.name}
                    checked={formik.values[o.name]}
                    onChange={() => {
                      formik.setFieldValue(o.name, !formik.values[o.name])
                      updateConfigurationOptions(o.name, !formik.values[o.name])
                    }}
                  />
                  <div className="ml-3 truncate">{o.label}</div>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center">
              <div className="w-full">
                {' '}
                <div className="text-sm my-[16px] opacity-50">Details JSON</div>
                <div className="bg-[#F5F5F5] w-full rounded-[10px] p-[16px]">
                  <div className="flex justify-between items-center">
                    <FileUpload
                      accept=".json,application/json"
                      label="Upload Details JSON"
                      onChange={(file) => {
                        if (!file) {
                          formik.setFieldValue('details_json', line?.details_json || null)
                          return
                        }
                        const reader = new FileReader()
                        reader.onload = (e) => {
                          try {
                            const jsonData = JSON.parse(e.target.result)
                            formik.setFieldValue('details_json', jsonData)
                          } catch (error) {
                            console.error('Failed to parse JSON file:', error)
                          }
                        }
                        reader.readAsText(file)
                      }}
                    />
                    {line?.details_json && !formik.values.details_json && (
                      <Button
                        variant="secondary"
                        size="small"
                        rounded
                        onClick={() => {
                          formik.setFieldValue('details_json', null)
                          setLine({ ...line, details_json: null })
                        }}
                      >
                        Delete JSON
                      </Button>
                    )}
                  </div>
                  {(formik.values.details_json || line?.details_json) && (
                    <div className="mt-[16px]">
                      <pre className="bg-[#cacaca] text-sm text-black rounded-[8px] p-[16px] max-h-[200px] overflow-y-auto">
                        {JSON.stringify(formik.values.details_json || line?.details_json, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>

              {/* <div>
                <div className="min-w-max flex items-center justify-center">
                  <Toggle
                    name="ready_for_publish"
                    checked={formik.values.ready_for_publish}
                    onChange={() => {
                      formik.setFieldValue('ready_for_publish', !formik.values.ready_for_publish)
                    }}
                  />
                  <div className="text-sm  opacity-50 ml-[16px]">Ready to publish</div>
                </div>
              </div> */}
            </div>
            <div
              className={`flex justify-end items-center mt-[16px] ${
                formik.isSubmitting ? 'pointer-events-none  opacity-50' : ''
              }`}
            >
              {id && (
                <Button rounded variant="secondary" onClick={deleteLine} className="mr-[16px]">
                  Delete
                </Button>
              )}
              <Button rounded variant="secondary" onClick={() => window.history.back()}>
                Cancel
              </Button>
              <Button loading={formik.isSubmitting} disabled={!isValid} rounded className="ml-[16px]" type="submit">
                {id ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </>
      )}
    </ScreenLayout>
  )
}

export default LineEditScreen
